import { watch } from "vue";
import { TYPE } from "vue-toastification";
import {
  authInfo,
  isTransientAuthError,
  isUserLoggedIn,
  logout,
  refreshTokens,
} from "@/core/auth";
import { getOrganizationRoles, organizationRoles } from "@/core/auth/user-role";
import { getParameter } from "@/core/util";
import { getConfiguration } from "@/configuration/services/configuration.service";
import { AlertId, dismissAlert, showAlert } from "@/core/popup";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "pointerdown",
  "pointermove",
  "wheel",
];

const ACTIVITY_LISTENER_OPTIONS = { capture: true, passive: true };

// Server token lifetimes, in milliseconds. The refresh token is short and
// sliding, so it is the real session bound: once it lapses the session is dead
// even though a request carrying the still-valid, longer-lived access token
// would still be accepted. These keys are not stored per-tenant, so the config
// lookup falls through to the server environment value (the same source the
// gateway's own @Value uses).
const JWT_REFRESH_KEY = "jwt.refresh-expiration";
const JWT_ACCESS_KEY = "jwt.expiration-time";
// Top of the config hierarchy: querying at this level skips the per-owner
// lookups and resolves straight from the environment fallback.
const TOP_CONFIG_LEVEL = "TENANT";

// Fallback only, used when neither the server property nor the
// SESSION_TIMEOUT_MINUTES parameter is available.
const DEFAULT_SESSION_MINUTES = 15;

const MIN_TOKEN_LIFETIME_MS = 60 * 1000;

const WARN_BEFORE_MS = 60 * 1000;

// Bounds on how often the watcher re-evaluates. It recomputes from absolute
// timestamps, so background-tab timer throttling only delays a tick, it does
// not drift. The effective interval is scaled down for short sessions so a
// sub-minute timeout is still detected in time (see resolveHeartbeatMs). These
// are client tuning knobs, overridable via the HEARTBEAT_MAX_MS /
// HEARTBEAT_MIN_MS parameters; the constants below are the defaults.
const DEFAULT_MAX_HEARTBEAT_MS = 20 * 1000;
const DEFAULT_MIN_HEARTBEAT_MS = 1000;

interface SessionSettings {
  sessionDurationMs: number;
  refreshAfterMs: number;
  maxHeartbeatMs: number;
  minHeartbeatMs: number;
}

const DEFAULT_TIMING = {
  sessionDurationMs: DEFAULT_SESSION_MINUTES * 60 * 1000,
  // Refresh once the token is half-way through its life, leaving ample margin
  // before the server-side refresh token expires.
  refreshAfterMs: (DEFAULT_SESSION_MINUTES * 60 * 1000) / 2,
};

const DEFAULT_BOUNDS = {
  maxHeartbeatMs: DEFAULT_MAX_HEARTBEAT_MS,
  minHeartbeatMs: DEFAULT_MIN_HEARTBEAT_MS,
};

let { maxHeartbeatMs, minHeartbeatMs } = DEFAULT_BOUNDS;
let { sessionDurationMs, refreshAfterMs } = DEFAULT_TIMING;

let lastActivityAt = Date.now();
let lastRefreshAt = Date.now();
let heartbeat: number | undefined;
let tracking = false;
let expiryWarningId: AlertId | undefined;
// Incremented by every arm and by every logout, so a load that completes after
// the session moved on can tell that it lost and must not commit.
let armVersion = 0;

// Poll a quarter of the session, clamped to the configured bounds. Tolerates a
// misconfigured min > max by never letting the floor exceed the ceiling.
export function resolveHeartbeatMs(
  sessionMs: number,
  minMs: number,
  maxMs: number
): number {
  const safeMin = Math.min(minMs, maxMs);
  return Math.min(maxMs, Math.max(safeMin, Math.floor(sessionMs / 4)));
}

function heartbeatMs(): number {
  return resolveHeartbeatMs(sessionDurationMs, minHeartbeatMs, maxHeartbeatMs);
}

function dismissExpiryWarning(): void {
  if (expiryWarningId !== undefined) {
    dismissAlert(expiryWarningId);
    expiryWarningId = undefined;
  }
}

function onUserActivity(): void {
  if (!isUserLoggedIn.value) {
    return;
  }
  lastActivityAt = Date.now();
  dismissExpiryWarning();
}

/**
 * Single source of truth for session lifetime. Runs on a heartbeat so expiry
 * is detected proactively — the user is redirected to login the moment the
 * idle window elapses, without having to click anything first.
 */
function checkSession(): void {
  if (!isUserLoggedIn.value) {
    stopSessionTracking();
    return;
  }

  const now = Date.now();

  // Idle past the session window -> log out proactively.
  if (now - lastActivityAt >= sessionDurationMs) {
    stopSessionTracking();
    logout();
    return;
  }

  const warnAtMs = Math.max(
    sessionDurationMs - WARN_BEFORE_MS,
    sessionDurationMs / 2
  );
  if (expiryWarningId === undefined && now - lastActivityAt >= warnAtMs) {
    expiryWarningId = showAlert(
      TYPE.WARNING,
      "Your session will expire soon. Any activity will keep you signed in.",
      { timeout: false }
    );
  }

  // Still within the active window: keep the token fresh ahead of expiry so
  // in-flight requests never fail and no logout races a successful save.
  if (now - lastRefreshAt >= refreshAfterMs) {
    const previousRefreshAt = lastRefreshAt;
    lastRefreshAt = now;
    refreshTokens().catch(async (error) => {
      if (isTransientAuthError(error)) {
        lastRefreshAt = previousRefreshAt;
        return;
      }
      stopSessionTracking();
      await logout();
    });
  }
}

function onVisibilityChange(): void {
  // A tab may have been frozen while hidden; re-evaluate immediately on return
  // so an expired session logs out at once instead of on the next heartbeat.
  if (document.visibilityState === "visible") {
    checkSession();
  }
}

export function startSessionTracking(): void {
  if (tracking) {
    return;
  }
  tracking = true;
  lastActivityAt = Date.now();
  lastRefreshAt = Date.now();

  ACTIVITY_EVENTS.forEach((event) => {
    window.addEventListener(event, onUserActivity, ACTIVITY_LISTENER_OPTIONS);
  });
  document.addEventListener("visibilitychange", onVisibilityChange);

  heartbeat = window.setInterval(checkSession, heartbeatMs());
}

export function stopSessionTracking(): void {
  tracking = false;

  ACTIVITY_EVENTS.forEach((event) => {
    window.removeEventListener(
      event,
      onUserActivity,
      ACTIVITY_LISTENER_OPTIONS
    );
  });
  document.removeEventListener("visibilitychange", onVisibilityChange);
  // Never let the warning outlive the session and follow the user to login.
  dismissExpiryWarning();

  if (heartbeat) {
    clearInterval(heartbeat);
    heartbeat = undefined;
  }
}

// Parses a millisecond duration from a raw property value, tolerating the
// underscore digit-grouping used in the server config (e.g. "900_000").
export function parseMs(raw: string | undefined | null): number | undefined {
  if (raw == null) {
    return undefined;
  }
  const value = Number(String(raw).replace(/[_\s]/g, ""));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

// Discards an implausibly short lifetime so the caller falls back instead.
function usableLifetime(ms: number | undefined): number | undefined {
  if (ms === undefined) {
    return undefined;
  }
  if (ms < MIN_TOKEN_LIFETIME_MS) {
    console.warn(`Ignoring implausible token lifetime: ${ms}ms`);
    return undefined;
  }
  return ms;
}

/**
 * Chooses the idle window and refresh cadence from the server token lifetimes.
 * The idle window follows the refresh token (the binding session bound); the
 * token is refreshed at half the life of whichever token expires first, so an
 * active session never lets either token lapse.
 */
export function resolveSessionTiming(
  refreshMs: number | undefined,
  accessMs: number | undefined,
  fallbackMs: number
): { sessionDurationMs: number; refreshAfterMs: number } {
  const usableRefreshMs = usableLifetime(refreshMs);
  const usableAccessMs = usableLifetime(accessMs);

  const sessionMs = usableRefreshMs ?? fallbackMs;
  const soonestExpiryMs = Math.min(
    usableRefreshMs ?? Infinity,
    usableAccessMs ?? Infinity
  );
  const cadenceBaseMs = Number.isFinite(soonestExpiryMs)
    ? soonestExpiryMs
    : sessionMs;
  return {
    sessionDurationMs: sessionMs,
    refreshAfterMs: Math.min(cadenceBaseMs, sessionMs) / 2,
  };
}

async function getConfiguredMs(key: string): Promise<number | undefined> {
  const ownerId = authInfo.value?.tenantId;
  if (!ownerId) {
    return undefined;
  }
  const config = await getConfiguration(key, TOP_CONFIG_LEVEL, ownerId);
  return parseMs(config?.value);
}

async function loadSessionTiming(): Promise<typeof DEFAULT_TIMING> {
  const configuredMinutes = Number(
    await getParameter("SESSION_TIMEOUT_MINUTES")
  );
  const fallbackMs =
    Number.isFinite(configuredMinutes) && configuredMinutes >= 1
      ? configuredMinutes * 60 * 1000
      : DEFAULT_TIMING.sessionDurationMs;

  const [refreshMs, accessMs] = await Promise.all([
    getConfiguredMs(JWT_REFRESH_KEY).catch(() => undefined),
    getConfiguredMs(JWT_ACCESS_KEY).catch(() => undefined),
  ]);

  return resolveSessionTiming(refreshMs, accessMs, fallbackMs);
}

async function getPositiveIntParam(
  key: string,
  fallback: number
): Promise<number> {
  const value = Number(await getParameter(key));
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

async function loadHeartbeatBounds(): Promise<typeof DEFAULT_BOUNDS> {
  const [max, min] = await Promise.all([
    getPositiveIntParam("HEARTBEAT_MAX_MS", DEFAULT_BOUNDS.maxHeartbeatMs),
    getPositiveIntParam("HEARTBEAT_MIN_MS", DEFAULT_BOUNDS.minHeartbeatMs),
  ]);
  return { maxHeartbeatMs: max, minHeartbeatMs: min };
}

async function loadSessionSettings(): Promise<SessionSettings> {
  const [timing, bounds] = await Promise.all([
    loadSessionTiming().catch(() => DEFAULT_TIMING),
    loadHeartbeatBounds().catch(() => DEFAULT_BOUNDS),
  ]);
  return { ...timing, ...bounds };
}

/**
 * Loads the session settings and, if this arm is still the current one, commits
 * them and starts tracking. Everything before the commit is network-bound, so
 * the session can end or be replaced while it is in flight.
 */
async function armSessionTracking(): Promise<void> {
  const version = ++armVersion;

  let settings: SessionSettings;
  try {
    settings = await loadSessionSettings();
  } catch (error) {
    console.error("Failed to load session settings, using defaults:", error);
    settings = { ...DEFAULT_TIMING, ...DEFAULT_BOUNDS };
  }

  if (version !== armVersion || !isUserLoggedIn.value) {
    return;
  }

  sessionDurationMs = settings.sessionDurationMs;
  refreshAfterMs = settings.refreshAfterMs;
  maxHeartbeatMs = settings.maxHeartbeatMs;
  minHeartbeatMs = settings.minHeartbeatMs;

  startSessionTracking();
}

watch(
  () => isUserLoggedIn.value,
  (loggedIn) => {
    if (!loggedIn) {
      armVersion += 1;
      stopSessionTracking();
      return;
    }
    armSessionTracking().catch((error) => {
      console.error("Session tracking could not be armed:", error);
    });
  }
);

export function init(): void {
  loadFeaturePackages();

  if (!isUserLoggedIn.value) {
    return;
  }

  refreshTokens()
    .catch((error) => {
      if (!isTransientAuthError(error)) {
        throw error;
      }
    })
    .then(async () => {
      if (!isUserLoggedIn.value) {
        return;
      }

      await armSessionTracking();

      if (!authInfo.value || organizationRoles.value.length > 0) {
        return;
      }

      const response = await getOrganizationRoles(authInfo.value.userId);
      if (!response) {
        stopSessionTracking();
        await logout();
      }
    })
    .catch((error) => {
      if (!isUserLoggedIn.value) {
        stopSessionTracking();
        return;
      }
      console.error("Session startup failed while still authenticated:", error);
    });
}

function loadFeaturePackages(): void {
  const files = require.context("@", true, /^.\/((?!\/).)+\/index\.(js|ts)$/);
  files.keys().forEach((file) => {
    files(file);
  });
}
